import React, { useEffect, useState } from "react";
import {
  WorkoutContainer,
  WorkoutTemplatePreview,
} from "../../libs/workout/types";
import {
  useDeleteWorkoutMutation,
  useMoveWorkoutToContainerMutation,
  useUpdateWorkoutContainerMutation,
} from "../../libs/workout/api";
import {
  ColorPicker,
  DEFAULT_CONTAINER_COLOR,
} from "../ProgressionsTable/ColorPicker";
import { WorkoutContainerCard } from "./WorkoutContainerCard";
import { WorkoutCard } from "./WorkoutCard";
import { CreateWorkoutContainerModal } from "./CreateWorkoutContainerModal";
import { DeleteWorkoutContainerModal } from "./DeleteWorkoutContainerModal";
import { CreateTemplateModal } from "./CreateTemplateModal";
import { WORKOUT_DRAG_TYPE } from "./WorkoutCard";

// Limite allineato al backend (fonte di verità).
// Solo la PROFONDITÀ di annidamento è limitata; il numero di sotto-cartelle
// per cartella è invece illimitato.
const MAX_DEPTH = 4; // livelli (cartella → sotto → sotto → sotto)

type Props = {
  templates: WorkoutTemplatePreview[];
  containers: WorkoutContainer[];
};

export const WorkoutFolders: React.FC<Props> = ({ templates, containers }) => {
  // Stack di id cartella dalla radice alla cartella corrente.
  // [] = radice; [a] = dentro a; [a,b] = dentro la sotto-cartella b; ecc.
  const [path, setPath] = useState<number[]>([]);
  const [search, setSearch] = useState("");

  const [openModal, setOpenModal] = useState<string | undefined>();
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  const [containerToDelete, setContainerToDelete] = useState<WorkoutContainer>();
  const [containerToRename, setContainerToRename] = useState<WorkoutContainer>();
  const [renameValue, setRenameValue] = useState("");
  const [renameColor, setRenameColor] = useState(DEFAULT_CONTAINER_COLOR);
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState(false);

  const [moveWorkout] = useMoveWorkoutToContainerMutation();
  const [updateContainer] = useUpdateWorkoutContainerMutation();
  const [deleteWorkout] = useDeleteWorkoutMutation();

  // Selezione multipla
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      for (const id of selectedIds) {
        await deleteWorkout({ id: Number(id) } as any).unwrap();
      }
    } catch (err) {
      console.error("Errore durante la cancellazione multipla:", err);
    } finally {
      setBulkDeleting(false);
      setConfirmBulkDelete(false);
      exitSelectionMode();
    }
  };

  // Se le cartelle cambiano (es. dopo delete) e un id nel path non esiste più,
  // tronca il path fino all'ultimo segmento ancora valido.
  useEffect(() => {
    const validUpTo = path.findIndex((id) => !containers.some((c) => c.id === id));
    if (validUpTo !== -1) setPath((p) => p.slice(0, validUpTo));
  }, [containers, path]);

  // Reset search quando si naviga (cambio cartella corrente).
  useEffect(() => setSearch(""), [path]);

  const childrenOf = (parentId: number) =>
    containers.filter((c) => c.parentId === parentId);
  const workoutsIn = (containerId: number) =>
    templates.filter((t) => t.idContainer === containerId);
  const orphanTemplates = templates.filter(
    (t) => t.idContainer == null || !containers.some((c) => c.id === t.idContainer)
  );

  const currentContainerId = path.length ? path[path.length - 1] : null;
  const currentContainer = containers.find((c) => c.id === currentContainerId);
  const currentDepth = path.length; // 0 = radice; 1..MAX_DEPTH dentro le cartelle

  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const matchesText = (s?: string | null) => (s ?? "").toLowerCase().includes(q);
  const matchesFolder = (c: WorkoutContainer) => matchesText(c.name);
  // La ricerca matcha sia il nome sia la descrizione (note) del workout.
  const matchesTemplate = (t: WorkoutTemplatePreview) =>
    matchesText(t.name) || matchesText(t.note);

  const containerColorOf = (t: WorkoutTemplatePreview) =>
    containers.find((c) => c.id === t.idContainer)?.color;

  // Ricostruisce il path completo dalla radice a una cartella qualsiasi
  // risalendo i parentId: serve per la navigazione normale e per aprire un
  // risultato di ricerca globale (cartella a qualsiasi livello).
  const pathTo = (c: WorkoutContainer): number[] => {
    const ids: number[] = [];
    let cur: WorkoutContainer | undefined = c;
    const guard = new Set<number>(); // evita loop su dati corrotti
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      ids.unshift(cur.id);
      cur =
        cur.parentId != null
          ? containers.find((x) => x.id === cur!.parentId)
          : undefined;
    }
    return ids;
  };

  const openFolder = (c: WorkoutContainer) => setPath(pathTo(c));

  // Ricerca GLOBALE: con un termine attivo si cerca in TUTTE le cartelle
  // (non solo in quella corrente) e anche nella descrizione.
  const foldersToShow = searching
    ? containers.filter(matchesFolder)
    : currentContainerId == null
    ? containers.filter((c) => c.parentId == null)
    : childrenOf(currentContainerId);

  const workoutsToShow = searching
    ? templates.filter(matchesTemplate)
    : currentContainerId == null
    ? orphanTemplates
    : workoutsIn(currentContainerId);

  const handleDropWorkout = (workoutId: string, targetContainerId: number | null) => {
    moveWorkout({ id: workoutId, idContainer: targetContainerId })
      .unwrap()
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Spostamento workout fallito:", err);
        const status = err?.status ?? "?";
        const body =
          typeof err?.data === "string"
            ? err.data
            : JSON.stringify(err?.data ?? err);
        alert(`Spostamento non riuscito (status ${status}): ${body}`);
      });
  };

  const openRename = (container: WorkoutContainer) => {
    setContainerToRename(container);
    setRenameValue(container.name);
    setRenameColor(container.color || DEFAULT_CONTAINER_COLOR);
    setRenameError(false);
    setOpenModal("renameWorkoutContainer");
  };

  const handleRenameSubmit = () => {
    if (!containerToRename || !renameValue.trim()) return;
    setRenameSaving(true);
    setRenameError(false);
    updateContainer({
      id: containerToRename.id,
      name: renameValue.trim(),
      color: renameColor,
      parentId: containerToRename.parentId ?? null,
    })
      .unwrap()
      .then(() => {
        setContainerToRename(undefined);
        setOpenModal(undefined);
        setRenameSaving(false);
      })
      .catch(() => {
        setRenameSaving(false);
        setRenameError(true);
      });
  };

  const depthLimitReached = currentDepth >= MAX_DEPTH;
  // Nessun limite sul numero di sotto-cartelle: si blocca solo alla profondità max.
  const canAddSubfolder = currentContainerId != null && !depthLimitReached;

  // Drop handler condiviso per i segmenti del breadcrumb (sposta il workout
  // nella cartella di quel segmento, o nella radice).
  const breadcrumbDropProps = (targetId: number | null) => ({
    onDragOver: (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes(WORKOUT_DRAG_TYPE)) e.preventDefault();
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const data = e.dataTransfer.getData(WORKOUT_DRAG_TYPE);
      if (!data) return;
      try {
        const dragged = JSON.parse(data) as {
          id: string;
          idContainer: number | null;
        };
        if (dragged.idContainer !== targetId) handleDropWorkout(dragged.id, targetId);
      } catch {}
    },
  });

  return (
    <div className="p-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4 text-sm flex-wrap">
        <button
          onClick={() => setPath([])}
          {...breadcrumbDropProps(null)}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Workout
        </button>
        {path.map((id, i) => {
          const c = containers.find((x) => x.id === id);
          const isLast = i === path.length - 1;
          return (
            <React.Fragment key={id}>
              <span className="text-gray-400">/</span>
              {isLast ? (
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                  {c?.name ?? "?"}
                </span>
              ) : (
                <button
                  onClick={() => setPath(path.slice(0, i + 1))}
                  {...breadcrumbDropProps(id)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {c?.name ?? "?"}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Actions bar */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
        <div className="relative w-full md:w-1/2">
          <input
            type="text"
            placeholder="Cerca in tutte le cartelle (nome o descrizione)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full py-2 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-3">
          {selectionMode ? (
            <>
              <button
                onClick={() => setConfirmBulkDelete(true)}
                disabled={selectedIds.size === 0}
                className="text-white bg-red-600 hover:bg-red-700 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Elimina ({selectedIds.size})
              </button>
              <button
                onClick={exitSelectionMode}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium text-sm px-4 py-2.5"
              >
                Annulla
              </button>
            </>
          ) : (
            <>
              {workoutsToShow.length > 0 && (
                <button
                  onClick={() => setSelectionMode(true)}
                  className="text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
                >
                  Seleziona
                </button>
              )}
              <button
                onClick={() => setTemplateModalOpen(true)}
                className="text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
              >
                Nuovo template
              </button>
              {currentContainerId == null ? (
                <button
                  onClick={() => {
                    setCreateParentId(null);
                    setOpenModal("createWorkoutContainer");
                  }}
                  className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
                >
                  Nuova cartella
                </button>
              ) : (
                <button
                  disabled={!canAddSubfolder}
                  title={
                    canAddSubfolder
                      ? undefined
                      : `Profondità massima: ${MAX_DEPTH} livelli`
                  }
                  onClick={() => {
                    setCreateParentId(currentContainerId);
                    setOpenModal("createWorkoutContainer");
                  }}
                  className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Nuova sotto-cartella
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Grid: cartelle poi workout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {foldersToShow.map((container) => {
          const subCount = childrenOf(container.id).length;
          const wkts = workoutsIn(container.id);
          return (
            <WorkoutContainerCard
              key={`c-${container.id}`}
              container={container}
              subCount={subCount}
              workoutCount={wkts.length}
              previewNames={wkts.map((w) => w.name)}
              onClick={() => openFolder(container)}
              onRename={() => openRename(container)}
              onDelete={() => {
                setContainerToDelete(container);
                setOpenModal("deleteWorkoutContainer");
              }}
              onDropWorkout={handleDropWorkout}
            />
          );
        })}

        {workoutsToShow.map((template) => (
          <WorkoutCard
            key={`w-${template.id}`}
            template={template}
            color={searching ? containerColorOf(template) : currentContainer?.color}
            onMoveToRoot={
              (searching ? template.idContainer != null : currentContainerId != null)
                ? () => handleDropWorkout(template.id, null)
                : undefined
            }
            selectionMode={selectionMode}
            selected={selectedIds.has(template.id)}
            onToggleSelect={() => toggleSelect(template.id)}
          />
        ))}

        {foldersToShow.length === 0 && workoutsToShow.length === 0 && (
          <div className="col-span-full text-center text-gray-400 py-8">
            {searching
              ? "Nessun risultato per la ricerca."
              : currentContainerId == null
              ? "Nessuna cartella. Creane una per organizzare i workout."
              : "Cartella vuota. Trascina qui i workout o creane di nuovi."}
          </div>
        )}
      </div>

      {/* Workout orfani: nota nella radice sotto le cartelle */}
      {!searching &&
        currentContainerId == null &&
        orphanTemplates.length > 0 &&
        foldersToShow.length > 0 && (
          <p className="mt-6 text-xs text-gray-400">
            I workout qui sotto non sono in nessuna cartella: trascinali su una
            cartella per organizzarli.
          </p>
        )}

      {/* Modals */}
      <CreateWorkoutContainerModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        parentId={createParentId}
      />
      <DeleteWorkoutContainerModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        container={containerToDelete}
        workoutsCount={
          containerToDelete ? workoutsIn(containerToDelete.id).length : 0
        }
        subCount={
          containerToDelete ? childrenOf(containerToDelete.id).length : 0
        }
      />
      <CreateTemplateModal
        openModal={templateModalOpen ? "create-template" : undefined}
        setOpenModal={(v) => setTemplateModalOpen(!!v)}
      />

      {/* Bulk delete confirmation modal */}
      {confirmBulkDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Elimina workout selezionati
            </h3>
            <p className="mb-4 text-gray-600 dark:text-gray-300">
              Vuoi eliminare definitivamente{" "}
              <strong>{selectedIds.size} workout</strong>? L&apos;operazione non
              è reversibile.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmBulkDelete(false)}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800"
              >
                Annulla
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {bulkDeleting ? "Eliminazione..." : "Elimina"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename modal */}
      {openModal === "renameWorkoutContainer" && containerToRename && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Rinomina cartella
            </h3>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="block w-full py-2 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameSubmit();
              }}
            />
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Colore
              </label>
              <ColorPicker value={renameColor} onChange={setRenameColor} />
            </div>
            {renameError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                Errore durante il salvataggio. Riprova.
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setOpenModal(undefined);
                  setContainerToRename(undefined);
                  setRenameError(false);
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800"
              >
                Annulla
              </button>
              <button
                onClick={handleRenameSubmit}
                disabled={renameSaving}
                className="px-4 py-2 text-sm text-white bg-primary-700 hover:bg-primary-800 rounded-lg disabled:opacity-50"
              >
                {renameSaving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
